import type { JSX } from 'react';
import { useCallback, useRef } from 'react';
import Select, {
	components,
	GroupBase,
	Options,
	OptionProps,
	MultiValueGenericProps,
	MenuListProps,
} from 'react-select';

import { IndeterminateCheckbox, SelectOption } from '@stern-ui/ui';

import './Select.css';

interface SelectGroupedOption {
	readonly label: string;
	readonly options: readonly SelectOption[];
}

export type SelectV2Props = {
	name: string;
	customLabel?: (data: SelectOption) => React.ReactElement;
	disabledHelpText?: string;
	isDisabled?: boolean;
	isLargeInput?: boolean;
	isMulti?: boolean;
	options: Options<SelectOption>;
	placeholder?: string;
	handleChange?: (newValue: SelectOption | SelectOption[]) => void | undefined;
	value: SelectOption | SelectOption[] | null; // <-- updated type
	isCompact?: boolean; // if true displays selected options in a "+X" format
	showSelectAll?: boolean; // if true shows a "Select All" option in multi-select mode
};

export const SelectV2 = ({
	name,
	customLabel,
	disabledHelpText,
	isDisabled = false,
	isLargeInput = false,
	isMulti = false,
	isCompact = false,
	showSelectAll = false,
	options,
	placeholder,
	handleChange,
	// The value is the value passed into SelectV2.
	value,
	...props
}: SelectV2Props) => {
	const selectedRef = useRef(null);

	const Option = (
		props: JSX.IntrinsicAttributes &
			OptionProps<unknown, boolean, GroupBase<unknown> & SelectGroupedOption>
	) => {
		return isMulti ? (
			<components.Option {...props}>
				<input type='checkbox' checked={props.isSelected} onChange={() => null} />{' '}
				<label>{customLabel ? customLabel(props.data as SelectOption) : props.label}</label>
			</components.Option>
		) : (
			<components.Option {...props}>{props.label}</components.Option>
		);
	};

	const dot = (color?: string) => {
		if (color) {
			return {
				':before': {
					backgroundColor: color,
					borderRadius: 6,
					content: '" "',
					display: 'inline-block',
					marginRight: 10,
					marginBottom: 2,
					height: 6,
					width: 6,
				},
			};
		} else {
			return {};
		}
	};

	const formatGroupLabel = (data: SelectGroupedOption) => (
		<div className='font-semibold text-primaryTextDark normal-case text-base pb-2'>
			{data.label}
		</div>
	);

	const MultiValueContainer = (props: MultiValueGenericProps) => {
		if (isCompact) {
			const selectedOptions = props.selectProps.value as SelectOption[];
			if (selectedOptions.length > 1) {
				// more than one selected, we only want to show the first one with a +X
				const label = props.data.label;
				const index = selectedOptions.findIndex((selected) => selected.label === label);
				if (index === 0) {
					return (
						<>
							<components.MultiValueContainer {...props} />
							<span className='rounded-sm bg-highlight text-base py-1 px-2 inline-block ml-1'>
								+{selectedOptions.length - 1}
							</span>
						</>
					);
				}
				return null;
			}
		}
		// if we've reached here, just return the default container
		return <components.MultiValueContainer {...props} />;
	};

	const setValue = (newValue: SelectOption | SelectOption[] | null) => {
		// newValue could be the full Option Object: {label: string, value: string}
		if (handleChange) {
			handleChange(newValue as SelectOption);
		} else {
			value = newValue as SelectOption | SelectOption[] | null;
		}
	};

	const MenuList = useCallback(
		(props: MenuListProps) => {
			if (showSelectAll) {
				const selectedOptions = props.selectProps.value as SelectOption[];
				const valueLength = Array.isArray(selectedOptions)
					? selectedOptions.length
					: selectedOptions
						? 1
						: 0;
				const selectAllHandler = () => {
					if (valueLength === options.length) {
						// all are selected, so unselect all
						setValue([]);
					} else {
						// select all options
						setValue(options as SelectOption[]);
					}
				};
				return (
					<components.MenuList {...props}>
						<div className='flex items-center justify-between px-[12px] pt-2 pb-4 gap-2 [&>input:checked+button]:!text-primaryTextDark border-b-[1px] border-secondaryBorder mb-3'>
							<div className='flex items-center gap-2'>
								<IndeterminateCheckbox
									{...{
										checked: valueLength === options.length,
										indeterminate:
											valueLength > 0 && valueLength < options.length,
										onChange: (e) => {
											e.stopPropagation();
										},
										className: 'text-primaryBrand rounded-[2px]',
										onClick: (e) => {
											selectAllHandler();
											e.stopPropagation();
										},
										role: 'switch',
										'aria-describedby': 'select-all-button',
									}}
								/>
								<button
									id='select-all-button'
									type='button'
									className='text-primaryTextDark font-normal text-base'
									onClick={(e) => {
										selectAllHandler();
										e.stopPropagation();
									}}
								>
									{valueLength === options.length ? 'Deselect all' : 'Select all'}
								</button>
							</div>
							<div className='text-sm'>{valueLength} selected</div>
						</div>
						{props.children}
					</components.MenuList>
				);
			}
			return <components.MenuList {...props}>{props.children}</components.MenuList>;
		},
		[options, showSelectAll]
	);

	const hideSelectedOptions = !isCompact && !showSelectAll;
	return (
		<>
			<Select
				name={name}
				inputId={name}
				classNames={{
					input: () => `custom-select__input-outer ${isLargeInput ? 'text-md' : ''}`,
					placeholder: () => (isLargeInput ? '!text-md' : ''),
					option: () =>
						`!flex items-center gap-2 !bg-white [&>input]:!text-primaryBrand [&>input]:!rounded-[2px] [&>label]:!font-normal ${isLargeInput ? '!text-md' : ''} ${hideSelectedOptions ? '!text-primaryTextDark' : '!text-secondaryTextDark [&>input:checked+label]:!text-primaryTextDark'}`,
					singleValue: () => (isLargeInput ? '!text-md' : ''),
					multiValue: () => '!bg-highlight !rounded-sm',
					multiValueLabel: () => '!text-base',
					control: () =>
						`${isDisabled ? '!bg-disabledInputBackground !text-disabledInputText' : ''}`,
				}}
				closeMenuOnSelect={!isMulti}
				components={{
					Option,
					MultiValueContainer,
					MenuList,
				}}
				defaultValue={value}
				formatGroupLabel={formatGroupLabel}
				isDisabled={isDisabled}
				isMulti={isMulti}
				isOptionDisabled={(option) => !!(option as SelectOption).disabled}
				menuPortalTarget={document.body}
				onChange={(newValue) => {
					// newValue is the full Option Object: {label: string, value: string}
					setValue(newValue as SelectOption | SelectOption[] | null);
				}}
				options={options}
				hideSelectedOptions={hideSelectedOptions}
				placeholder={placeholder}
				ref={selectedRef}
				styles={{
					container: (base) => ({
						...base,
						marginTop: 4,
						borderColor: 'hsla(240, 3%, 36%, 1)',
						borderRadius: 1,
					}),
					control: (base) => ({
						...base,
						paddingTop: 3,
						paddingBottom: 3,
						paddingLeft: 4,
						paddingRight: 4,
						borderColor: 'hsla(213, 21%, 42%, 1)', // Select border
						backgroundColor: 'hsla(0, 0%, 100%, 1)',
						'&:hover': {
							borderColor: 'hsla(213, 21%, 42%, 1)',
						},
					}),
					option: (base, { data }) => ({
						...base,
						fontSize: 14,
						...dot((data as SelectOption).statusColor),
					}),
					singleValue: (base, { data }) =>
						isDisabled
							? {
									...base,
									fontSize: 14,
									color: 'hsla(240, 1%, 64%, 1)',
									...dot((data as SelectOption).statusColor),
								}
							: {
									...base,
									fontSize: 14,
									color: 'hsla(240, 3%, 36%, 1)',
									...dot((data as SelectOption).statusColor),
								},
					menuPortal: (base) => ({ ...base, zIndex: 9999 }),
					placeholder: (base) => ({
						...base,
						color: 'hsla(213, 21%, 42%, 1)',
						fontSize: 14,
						fontFamily: 'Montserrat',
					}),
					indicatorSeparator: (base) => ({
						...base,
						display: 'none',
					}),
					dropdownIndicator: (base) => ({
						...base,
						color: 'hsla(213, 21%, 42%, 1)',
					}),
				}}
				value={value}
				{...props}
			/>
			{isDisabled && disabledHelpText && (
				<p className='mt-2 text-base text-secondaryTextDark'>{disabledHelpText}</p>
			)}
		</>
	);
};
